import { apiRequest } from '@/lib/api/client';
import type {
  AcademicContact,
  CommunicationsOverview,
  ConversationDetail,
  ConversationMessage,
  DirectConversationPayload,
  GroupConversationPayload,
  OwnerMailHubData,
  OwnerMailItem,
  SendMessagePayload,
  UpdateOwnerMailPayload,
} from './types';

export const getCommunicationsOverview = (
  roleContext: string = 'SYSTEM_OWNER'
): Promise<CommunicationsOverview> =>
  apiRequest<CommunicationsOverview>('/communications/overview', {
    channel: 'platform',
    query: { role_context: roleContext },
  });

export const getAcademicContacts = (params?: {
  role_context?: string;
  category?: string;
  sub_filter?: string;
  search?: string;
}): Promise<AcademicContact[]> =>
  apiRequest<AcademicContact[]>('/communications/contacts', {
    channel: 'platform',
    query: {
      role_context: params?.role_context || 'SYSTEM_OWNER',
      category: params?.category,
      sub_filter: params?.sub_filter,
      search: params?.search,
    },
  });

export const getGroupConversations = (
  roleContext: string = 'SYSTEM_OWNER'
): Promise<AcademicContact[]> =>
  apiRequest<AcademicContact[]>('/communications/groups', {
    channel: 'platform',
    query: { role_context: roleContext },
  });

export const createGroupConversation = (
  payload: GroupConversationPayload
): Promise<ConversationDetail> =>
  apiRequest<ConversationDetail>('/communications/groups', {
    method: 'POST',
    channel: 'platform',
    body: payload,
  });

export const resolveDirectConversation = (
  payload: DirectConversationPayload,
  roleContext: string = 'SYSTEM_OWNER'
): Promise<ConversationDetail> =>
  apiRequest<ConversationDetail>('/communications/direct', {
    method: 'POST',
    channel: 'platform',
    query: { role_context: roleContext },
    body: payload,
  });

export const getConversationDetail = (
  conversationId: string
): Promise<ConversationDetail> =>
  apiRequest<ConversationDetail>(`/communications/conversations/${conversationId}`, {
    channel: 'platform',
  });

export const sendMessage = (
  conversationId: string,
  payload: SendMessagePayload
): Promise<ConversationMessage> =>
  apiRequest<ConversationMessage>(
    `/communications/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      channel: 'platform',
      body: payload,
    }
  );

export const toggleConversationPin = (
  conversationId: string
): Promise<{ is_pinned: boolean }> =>
  apiRequest<{ is_pinned: boolean }>(
    `/communications/conversations/${conversationId}/pin`,
    {
      method: 'POST',
      channel: 'platform',
    }
  );

export const markConversationAsRead = (
  conversationId: string
): Promise<{ status: string }> =>
  apiRequest<{ status: string }>(
    `/communications/conversations/${conversationId}/read`,
    {
      method: 'POST',
      channel: 'platform',
    }
  );

export const getOwnerMailHub = (params?: {
  account_id?: string;
  folder?: string;
  search?: string;
}): Promise<OwnerMailHubData> =>
  apiRequest<OwnerMailHubData>('/communications/owner-mail', {
    channel: 'platform',
    query: {
      account_id: params?.account_id,
      folder: params?.folder,
      search: params?.search,
    },
  });

export const getOwnerMailItem = (mailId: string): Promise<OwnerMailItem> =>
  apiRequest<OwnerMailItem>(`/communications/owner-mail/${mailId}`, {
    channel: 'platform',
  });

export const updateOwnerMailItem = (
  mailId: string,
  payload: UpdateOwnerMailPayload
): Promise<OwnerMailItem> =>
  apiRequest<OwnerMailItem>(`/communications/owner-mail/${mailId}`, {
    method: 'PATCH',
    channel: 'platform',
    body: payload,
  });
